import React, { useState, useEffect, useMemo } from 'react'
import injectStyles from 'react-jss'

import { styles } from './styles'
import { IconButton, Dialog, DialogTitle } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { STEPS } from './constants'
import ModalFirstStep from './ModalFirstStep'
import ModalSecondStep from './ModalSecondStep'
import ModalProgressStep from './ModalProgressStep'
import {
  getTitleComponent,
  toLocalISODate,
  getMissionTimeByType,
  splitMissionsByChunksForTLA,
} from './utils'
import ModalLastStep from './ModalLastStep'
import { MISSION_ENUM_TYPES } from '../../../../../shared/constants'
import {
  getPostcodesByAIGeneration,
  generateAIMissions,
  createMissions,
} from './gql'

const GenerateMissionsModal = ({
  classes,
  onClose,
  isOpen,
  onGenerate,
  campaign,
  city,
  cityPlan,
  onRefetch,
  dateRange,
}) => {
  const todayDate = useMemo(() => toLocalISODate(new Date()), [])
  const startDate = useMemo(() => toLocalISODate(dateRange.startDate), [
    dateRange.startDate,
  ])
  const maxDate = useMemo(
    () =>
      toLocalISODate(
        new Date(dateRange.endDate.getTime() - 24 * 60 * 60 * 1000),
      ),
    [dateRange.endDate],
  )
  const minDate = useMemo(() => [todayDate, startDate].sort().reverse()[0], [
    todayDate,
    startDate,
  ])

  const [currentStep, setCurrentStep] = useState(STEPS.FIRST)
  const [values, setValues] = useState({
    countOfMissions: 6,
    basPerMission: 2,
    startDate: minDate,
  })
  const [selectedPostcodes, setSelectedPostcodes] = useState([])
  const [selectedLocations, setSelectedLocations] = useState([])
  const [postcodeResults, setPostcodeResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [isDistributionZoneExists, setIsDistributionZoneExists] = useState(true)
  const [progress, setProgress] = useState({
    text: '',
    percentage: 0,
  })

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getPostcodesByAIGeneration(city.id)

      const {
        data: {
          city: {
            postcodesAndLocationsInsideDistributionZones: {
              postcodes,
              isDistributionZoneExists,
            },
          },
        },
      } = await response.json()

      setPostcodeResults(
        postcodes.sort((a, b) => b.locations.length - a.locations.length),
      )
      setIsDistributionZoneExists(isDistributionZoneExists)
    } catch (error) {
      console.error(error)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const locationsCount = useMemo(
    () =>
      selectedPostcodes.reduce((acc, item) => acc + item.locations.length, 0),
    [selectedPostcodes.length],
  )

  const onProgress = ({ processedAmount, totalAmount }) => {
    const percentage = Math.round((processedAmount / totalAmount) * 100)
    setProgress({
      percentage,
      text: `${processedAmount} from ${totalAmount} missions created (${percentage}%)`,
    })
  }

  const handleGenerate = async () => {
    setCurrentStep(STEPS.PROGRESS)
    setLoading(true)
    setProgress({
      text: 'Getting AI data...',
      percentage: 0,
    })

    const getPostcodesLocationsIds = data =>
      data.reduce((acc, item) => {
        if (item.locations.length) {
          item.locations.forEach(l => {
            acc.push(Number(l.id))
          })
        }
        return acc
      }, [])

    const authorizedLocationIds = selectedPostcodes.length
      ? getPostcodesLocationsIds(selectedPostcodes)
      : getPostcodesLocationsIds(postcodeResults)

    const forcedLocationIds = selectedLocations.map(l => Number(l.id))

    try {
      const result = await generateAIMissions({
        campaignId: campaign.id,
        cityName: city.name,
        cityId: city.id,
        countOfMissions: values.countOfMissions,
        startDate: values.startDate,
        authorizedLocationIds,
        forcedLocationIds,
      })
      if (!result) {
        setCurrentStep(STEPS.ERROR)
        setMessage('No missions were generated')
        return
      }

      const missionsToGenerate = result.missions.reduce((acc, m) => {
        for (let i = 1; i <= values.basPerMission; i++) {
          const time = getMissionTimeByType({
            city,
            type: m.time_of_day,
          })

          const dateTime = `${m.mission_date}T${time}.000`

          const data = {
            cityPlanId: cityPlan.id,
            locationId: m.location_id,
            draftPayAdjustment: NaN,
            dateTime,
            type: MISSION_ENUM_TYPES.HANDTOHAND,
            automatic: true,
            cvrPredictionPercent: m.score || '-',
          }

          acc.push(data)
        }
        return acc
      }, [])

      setProgress({
        text: 'Grouping missions...',
        percentage: 0,
      })

      const missionsByChunks = splitMissionsByChunksForTLA(missionsToGenerate)

      setProgress({
        percentage: 0,
        text: `0 from ${missionsToGenerate.length} missions created (0%)`,
      })

      const missions = await createMissions({
        missionsByChunks,
        campaignId: campaign.id,
        onProgress,
        totalAmount: missionsToGenerate.length,
      })

      if (result.message) {
        setMessage(result.message)
      }

      if (result.status === 'success') {
        setCurrentStep(STEPS.SUCCESS)
      } else {
        setCurrentStep(STEPS.ERROR)
        return
      }

      onGenerate(missions)
    } catch (error) {
      console.error({ error })
      setMessage(error)
      setCurrentStep(STEPS.ERROR)
      return
    } finally {
      setLoading(false)
    }
  }

  const isHideCloseButton = [
    STEPS.SUCCESS,
    STEPS.ERROR,
    STEPS.PROGRESS,
  ].includes(currentStep)

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className={classes.modal}
      disableBackdropClick={isHideCloseButton}
      disableEscapeKeyDown={isHideCloseButton}
    >
      <DialogTitle>
        <div className={classes.titleContainer}>
          {isHideCloseButton ? (
            <div className={classes.mockHeight} />
          ) : (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </DialogTitle>
      {getTitleComponent({
        classes,
        currentStep,
      })}
      {currentStep === STEPS.PROGRESS ? (
        <ModalProgressStep classes={classes} progress={progress} />
      ) : null}
      {currentStep === STEPS.FIRST ? (
        <ModalFirstStep
          classes={classes}
          setValues={setValues}
          values={values}
          setCurrentStep={setCurrentStep}
          selectedPostcodes={selectedPostcodes}
          setSelectedPostcodes={setSelectedPostcodes}
          onClose={onClose}
          postcodeResults={postcodeResults}
          loading={loading}
          locationsCount={locationsCount}
          handleGenerate={handleGenerate}
          isDistributionZoneExists={isDistributionZoneExists}
          dateRange={{ minDate, maxDate }}
        />
      ) : null}
      {currentStep === STEPS.SECOND ? (
        <ModalSecondStep
          classes={classes}
          selectedPostcodes={selectedPostcodes}
          setCurrentStep={setCurrentStep}
          handleGenerate={handleGenerate}
          values={values}
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          postcodeResults={postcodeResults}
          loading={loading}
          locationsCount={locationsCount}
        />
      ) : null}
      {[STEPS.SUCCESS, STEPS.ERROR].includes(currentStep) ? (
        <ModalLastStep
          classes={classes}
          currentStep={currentStep}
          message={message}
          onClose={onClose}
          onRefetch={onRefetch}
        />
      ) : null}
    </Dialog>
  )
}

export default injectStyles(styles)(GenerateMissionsModal)
